import {
  FC,
  ReactEventHandler,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Button from '@material-ui/core/Button';
import {
  makeStyles,
  createStyles,
  Theme,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import StaffTempRegistrationDialog from 'components/molecules/StaffTempRegistrationDialog';
import StaffDeleteDialog from 'components/molecules/StaffDeleteDialog';
import { Table, Button as Button2 } from 'antd';
import { StaffListModel } from 'types/User';
import { API_URL } from 'utils/CommonConst';
import MessagePanel, { Mode } from 'components/molecules/MessagePanel';
import StaffEditDialog from 'components/molecules/StaffEditDialog';
import { consoleLog, methodGet } from 'utils/CommonFunctions';
import { AppContext } from 'utils/AppContext';
import CustomBackdrop from 'components/molecules/CustomBackdrop';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@material-ui/core';

const theme2 = createTheme({
  overrides: {
    MuiButton: {
      containedPrimary: {
        backgroundColor: 'orange',
      },
    },
  },
});

const theme3 = createTheme({
  overrides: {
    MuiButton: {
      containedPrimary: {
        backgroundColor: 'gray',
      },
    },
    MuiDialogActions: {
      root: {
        justifyContent: 'center',
      },
    },
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      marginTop: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'left',
      fontSize: '18px',
    },
    submit: {
      maxWidth: 100,
      margin: theme.spacing(3, 0, 2),
      '&:hover': {
        background: 'darkorange',
      },
    },
    remail: {
      '&:active': {
        background: 'green',
      },
      '&:focus': {
        background: 'green',
      },
    },
    space: {
      margin: theme.spacing(0, 2, 0),
    },
    color: {
      backgroundColor: '#87cefa',
    },
    dialogTitle: {
      fontSize: 24,
    },
    cancel: {
      maxWidth: 110,
      '&:hover': {
        background: 'darkgray',
      },
    },
    cancelpos: {
      textAlign: 'right',
      marginRight: theme.spacing(0),
    },
  }),
);

export type StaffManagementDialogProps = {
  open: boolean;
  onClickClose: ReactEventHandler<HTMLButtonElement>;
  idCust: number;
};

const StaffManagementDialog: FC<StaffManagementDialogProps> = ({
  open,
  onClickClose,
  idCust,
}) => {
  const appContext = useContext(AppContext);
  const classes = useStyles();
  const [staffsData, setStaffsData] = useState<StaffListModel[]>([]);
  const [staffData, setStaffData] = useState<StaffListModel>();
  const [isLoading, setIsLoading] = useState(true);
  const [isHide, setIsHide] = useState<boolean>(true);
  const [errorMessages, setErrorMessages] = useState<string[]>();
  const [mode, setMode] = useState<Mode>('info');

  const getData = useCallback(async () => {
    if (appContext.state.LoginUser.isLogin) {
      try {
        const url = `${API_URL}api/v1/users/${idCust ?? 0}/staffs`;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const res = await methodGet(url, true, appContext.state.LoginUser);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const data = res.data as StaffListModel[];
        setStaffsData(data);
        setIsLoading(false);
      } catch (ex) {
        consoleLog(ex);
      }
    }
  }, [appContext.state.LoginUser, idCust]);
  useEffect(() => {
    getData().finally(() => {
      /* Nothing to do. */
    });
  }, [getData]);

  const text =
    '担当者登録用にメールを送信します。 ¥n メールに記載されたURLにアクセスし、ログインすると登録が完了します。';
  const breakedText = text.split('¥n').map((line) => (
    <span key={line}>
      {line}
      <br />
    </span>
  ));

  const [openTemp, setOpenTemp] = useState(false);

  const handleClickOpenTemp = () => {
    setOpenTemp(true);
  };

  const handleCloseTemp = (handleClickInsert: boolean) => {
    setOpenTemp(false);
    if (handleClickInsert) {
      getData().finally(() => {
        /* Nothing to do. */
      });
    }
  };

  const [openDelete, setOpenDelete] = useState(false);

  const handleClickOpenDelete = (row: StaffListModel) => {
    setStaffData(row);
    setOpenDelete(true);
  };

  const handleCloseDelete = (isDeleted: boolean) => {
    setOpenDelete(false);
    if (isDeleted) {
      getData().finally(() => {
        /* Nothing to do. */
      });
    }
  };

  const [openEdit, setOpenEdit] = useState(false);

  const handleClickOpenEdit = (row: StaffListModel) => {
    setStaffData(row);
    setOpenEdit(true);
  };

  const handleCloseEdit = (isEdit: boolean, changeMail: boolean) => {
    setOpenEdit(false);
    if (changeMail) {
      setIsHide(false);
      setMode('info');
      setErrorMessages([
        `担当者「${
          staffData?.name ?? ''
        }」のメールアドレスに認証メールを送信しました。`,
      ]);
    }
    if (isEdit) {
      getData().finally(() => {
        /* Nothing to do. */
      });
    }
  };

  const columns = [
    {
      title: '担当者名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      align: 'center' as const,
    },
    {
      title: '所属',
      dataIndex: 'section',
      key: 'section',
      align: 'center' as const,
    },
    {
      title: 'メールアドレス',
      dataIndex: 'mail',
      key: 'mail',
      align: 'center' as const,
      render: (value: string) => (
        <span className="email-text">{value || 'なし'}</span>
      ),
    },
    {
      title: '電話番号',
      dataIndex: 'tel',
      key: 'tel',
      align: 'center' as const,
    },
    {
      title: '状態',
      dataIndex: 'isEnable',
      key: 'isEnable',
      width: 150,
      align: 'center' as const,
      render: (value: boolean) => (
        <>{value ? <span>登録済</span> : <span>仮登録</span>}</>
      ),
    },
    {
      title: '削除',
      dataIndex: 'isDeleted',
      key: 'isDeleted',
      render: (value: boolean, row: StaffListModel) => (
        <>
          <span className="invoiceUpload-button">
            <Button2
              type="primary"
              className={classes.space}
              onClick={() => {
                handleClickOpenEdit(row);
              }}
            >
              編集
            </Button2>
          </span>
          <Button2
            type="primary"
            danger
            className={classes.space}
            onClick={() => {
              handleClickOpenDelete(row);
            }}
          >
            削除
          </Button2>
        </>
      ),
      align: 'center' as const,
    },
  ];

  // ダイアログを閉じる
  const dialogClose = (event: SyntheticEvent<HTMLButtonElement>) => {
    setIsHide(true);
    setErrorMessages(['']);
    onClickClose(event);
  };

  return (
    <>
      <Dialog maxWidth="lg" open={open} onClose={dialogClose}>
        <DialogTitle className={classes.color}>
          <Box className={classes.dialogTitle}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                担当者管理
              </Grid>
              <Grid item xs={6}>
                <div className={classes.cancelpos}>
                  <MuiThemeProvider theme={theme3}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.cancel}
                      onClick={dialogClose}
                    >
                      閉じる
                    </Button>
                  </MuiThemeProvider>
                </div>
              </Grid>
            </Grid>
          </Box>
        </DialogTitle>
        <DialogContent>
          <MessagePanel messages={errorMessages} isHide={isHide} mode={mode} />
          <div hidden={isLoading}>
            <MuiThemeProvider theme={theme2}>
              <Button
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={handleClickOpenTemp}
              >
                追加
              </Button>
            </MuiThemeProvider>
            <StaffTempRegistrationDialog
              open={openTemp}
              onClickClose={handleCloseTemp}
              text={breakedText}
              button="登録"
              register
              idCust={idCust}
            />
            <StaffDeleteDialog
              open={openDelete}
              onClickClose={handleCloseDelete}
              row={staffData}
              staffDelete
            />
            <StaffEditDialog
              open={openEdit}
              onClickClose={handleCloseEdit}
              row={staffData}
            />
            <div className="table-size">
              <Table
                dataSource={staffsData}
                columns={columns}
                pagination={{ pageSize: 20 }}
                rowKey="picID"
              />
            </div>
          </div>
        </DialogContent>
        <CustomBackdrop open={isLoading} />
      </Dialog>
    </>
  );
};

export default StaffManagementDialog;
